import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useStudentProfileStore } from '../../stateManagers/stores/studentProfileStore';

/* ─── Design tokens ─────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
  amber:       '#f59e0b',
  amberDim:    'rgba(245,158,11,0.12)',
  amberBorder: 'rgba(245,158,11,0.35)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface Batch {
  batch_id:    string;
  batch_name:  string;
  course_name?: string | null;
}

interface AssignmentFile {
  file_name:   string;
  key:         string;
  url:         string;
  size:        number;
  mime_type:   string;
  uploaded_at: string;
}

interface Assignment {
  assignment_id:          string;
  institute_id:           string;
  institute_name:         string;
  branch_id:              string;
  branch_name:            string;
  batch_id:               string | null;
  batch_name:             string | null;
  assignment_title:       string;
  assignment_body:        string;
  files:                  AssignmentFile[];
  start_date:             string | null;
  start_time:             string | null;
  submission_date:        string | null;
  submission_time:        string | null;
  allow_late_submissions: boolean;
  notes:                  string;
  created_at:             string;
  created_by:             string;
  created_by_name:        string;
}

interface Pagination {
  total: number;
  page:  number;
  limit: number;
  pages: number;
}

interface ApiResponse {
  success:    boolean;
  data:       Assignment[];
  pagination: Pagination;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */
const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (a: Assignment) => {
  if (!a.submission_date) return false;
  return new Date() > new Date(a.submission_date) && !a.allow_late_submissions;
};

const isDueSoon = (a: Assignment) => {
  if (!a.submission_date) return false;
  const hrs = (new Date(a.submission_date).getTime() - Date.now()) / 3_600_000;
  return hrs > 0 && hrs <= 24;
};

/* ─── AssignmentCard ─────────────────────────────────────────────────── */
const AssignmentCard: React.FC<{ assignment: Assignment; onPress: (a: Assignment) => void }> = ({ assignment, onPress }) => {
  const overdue    = isOverdue(assignment);
  const dueSoon    = isDueSoon(assignment);
  const hasBatch   = assignment.batch_id !== null;
  const filesCount = assignment.files?.length ?? 0;

  const accentColor = overdue ? C.red : dueSoon ? C.amber : C.navy;
  const accentGrad  = overdue
    ? `linear-gradient(90deg, ${C.red} 0%, #ef4444 100%)`
    : dueSoon
    ? `linear-gradient(90deg, ${C.amber} 0%, #fbbf24 100%)`
    : `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 100%)`;

  return (
    <>
      <style>{`
        .asgn-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
        .asgn-card:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(14,34,93,0.13) !important; }
        .asgn-view-btn { transition: all 0.15s ease; }
        .asgn-view-btn:hover { background: ${C.navy} !important; color: ${C.white} !important; }
        .asgn-view-btn:hover .asgn-arrow { background: ${C.gold} !important; }
      `}</style>
      <div
        className="asgn-card"
        style={{
          background:    C.white,
          borderRadius:  '16px',
          overflow:      'hidden',
          border:        `1px solid ${C.border}`,
          boxShadow:     '0 2px 8px rgba(14,34,93,0.06)',
          cursor:        'pointer',
          position:      'relative',
        }}
        onClick={() => onPress(assignment)}
      >
        {/* Top accent bar */}
        <div style={{ height: '3px', background: accentGrad }} />

        <div style={{ padding: '18px 20px' }}>

          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '12px' }}>

            {/* Icon */}
            <div style={{
              width: '48px', height: '48px', flexShrink: 0, borderRadius: '14px',
              background: overdue ? C.redDim : dueSoon ? C.amberDim : 'rgba(14,34,93,0.08)',
              border: `2px solid ${overdue ? C.redBorder : dueSoon ? C.amberBorder : C.goldBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="pi pi-file-edit" style={{ fontSize: '1.2rem', color: accentColor }} />
            </div>

            {/* Title + batch badge */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.92rem', fontWeight: '700', color: C.navy, lineHeight: 1.4, wordBreak: 'break-word' }}>
                {assignment.assignment_title}
              </p>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', borderRadius: '6px', border: `1px solid ${hasBatch ? C.navy : C.greenBorder}`, background: hasBatch ? 'rgba(14,34,93,0.06)' : C.greenDim }}>
                <i className={`pi ${hasBatch ? 'pi-users' : 'pi-globe'}`} style={{ fontSize: '0.65rem', color: hasBatch ? C.navy : C.green }} />
                <span style={{ fontSize: '0.7rem', fontWeight: '700', color: hasBatch ? C.navy : C.green }}>
                  {assignment.batch_name ?? 'All Batches'}
                </span>
              </div>
            </div>

            {/* Status badge */}
            {overdue ? (
              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', background: C.redDim, border: `1px solid ${C.redBorder}`, fontSize: '0.7rem', fontWeight: '700', color: C.red }}>
                <i className="pi pi-exclamation-circle" style={{ fontSize: '0.65rem' }} /> Overdue
              </span>
            ) : dueSoon ? (
              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', background: C.amberDim, border: `1px solid ${C.amberBorder}`, fontSize: '0.7rem', fontWeight: '700', color: C.amber }}>
                <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} /> Due Soon
              </span>
            ) : assignment.allow_late_submissions ? (
              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', background: C.greenDim, border: `1px solid ${C.greenBorder}`, fontSize: '0.7rem', fontWeight: '700', color: C.green }}>
                <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} /> Late OK
              </span>
            ) : null}
          </div>

          {/* Body preview */}
          {assignment.assignment_body && (
            <p style={{ margin: '0 0 12px 0', fontSize: '0.83rem', color: C.sub, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {assignment.assignment_body}
            </p>
          )}

          {/* Dates row */}
          <div style={{ display: 'flex', gap: '0', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '10px 0', margin: '0 0 12px 0' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-calendar" style={{ fontSize: '0.85rem', color: C.navy }} />
              <div>
                <p style={{ margin: 0, fontSize: '0.62rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Start</p>
                <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '600', color: C.body }}>{formatDate(assignment.start_date)}</p>
              </div>
            </div>
            <div style={{ width: '1px', background: C.border, margin: '0 16px' }} />
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-clock" style={{ fontSize: '0.85rem', color: overdue ? C.red : dueSoon ? C.amber : C.gold }} />
              <div>
                <p style={{ margin: 0, fontSize: '0.62rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Due</p>
                <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '600', color: overdue ? C.red : dueSoon ? C.amber : C.body }}>
                  {formatDate(assignment.submission_date)}
                  {assignment.submission_time ? ` · ${assignment.submission_time}` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Files chip */}
          {filesCount > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 10px', borderRadius: '8px', background: 'rgba(14,34,93,0.06)', marginBottom: '12px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'rgba(14,34,93,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-paperclip" style={{ fontSize: '0.75rem', color: C.navy }} />
              </div>
              <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.navy }}>
                {filesCount} {filesCount === 1 ? 'File' : 'Files'} Attached
              </span>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <i className="pi pi-user" style={{ fontSize: '0.75rem', color: C.muted }} />
              <span style={{ fontSize: '0.75rem', color: C.muted }}>By {assignment.created_by_name}</span>
            </div>
            <button
              className="asgn-view-btn"
              onClick={e => { e.stopPropagation(); onPress(assignment); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 14px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.navy, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}
            >
              View Details
              <span className="asgn-arrow" style={{ width: '22px', height: '22px', borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
                <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem', color: C.navy }} />
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const Assignments: React.FC = () => {
  const navigate = useNavigate();
  const toast    = useRef<Toast>(null);

  // ── Read from store directly (same pattern as ClassTestList) ──
  const { profile } = useStudentProfileStore();

  const branchId = profile?.branch?.branch_id ?? null;

  // Derive batches from the student's enrollments — no separate API call.
  // Still used to render the filter chips; no longer used to drive the fetch itself.
  const batches: Batch[] = (profile?.enrollments ?? [])
    .filter(e => e.batch_id !== null && e.batch_id !== undefined && e.batch_id.trim() !== '')
    .map(e => ({
      batch_id:    e.batch_id as string,
      batch_name:  e.batch_name ?? 'Batch',
      course_name: e.course_name ?? null,
    }));
  const batchesLoading = false;

  // Derive isEnrolled from batches
  const isEnrolled = batches.length > 0;

  const [assignments,   setAssignments]   = useState<Assignment[]>([]);
  const [pagination,    setPagination]    = useState<Pagination>({ total: 0, page: 1, limit: 10, pages: 0 });
  const [loading,       setLoading]       = useState(false);
  const [page,          setPage]          = useState(1);
  const [limit]         = useState(10);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null); // null = all batches

  /* ── Fetch assignments whenever filters/page change ──
     Server now does the enrollment scoping (my-enrollments), so we don't
     depend on branchId being loaded — just call it and let the backend
     resolve the student from the auth token. */
  useEffect(() => {
    fetchAssignments();
  }, [page, limit, selectedBatch]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });

      const res = await apiClient.get<ApiResponse>(`/v1/assignments/my-enrollments?${params}`);

      if (res.data.success) {
        // selectedBatch narrows the already-fetched, already-enrollment-scoped
        // page client-side. "All Batches" (null) also includes the general
        // (batch_id === null) assignments returned by the endpoint.
        const data = selectedBatch
          ? res.data.data.filter(a => a.batch_id === selectedBatch || a.batch_id === null)
          : res.data.data;

        setAssignments(data);
        setPagination(res.data.pagination);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load assignments', life: 3000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to load assignments', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSelect = (batchId: string | null) => {
    setSelectedBatch(batchId);
    setPage(1);
  };

  const handleAssignmentPress = (a: Assignment) => {
    navigate(`/assignments/${a.assignment_id}`);
  };

  /* ── Pagination ── */
  const renderPagination = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }
    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{ minWidth: '32px', height: '32px', padding: '0 8px', borderRadius: '7px', fontSize: '0.82rem', fontWeight: page === p ? '700' : '500', background: page === p ? C.navy : 'transparent', color: page === p ? C.gold : C.navy, border: page === p ? 'none' : `1px solid ${C.border}`, boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none', cursor: 'pointer', transition: 'all 0.15s ease' }}
          onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  return (
    <>
      <style>{`
        .asgn-batch-chips { display: flex; gap: 6px; flex-wrap: wrap; overflow-x: auto; padding-bottom: 2px; }
        .asgn-batch-chips::-webkit-scrollbar { height: 3px; }
        .asgn-batch-chips::-webkit-scrollbar-track { background: transparent; }
        .asgn-batch-chips::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        .asgn-batch-chip { white-space: nowrap; transition: all 0.15s ease; }
        .asgn-batch-chip:hover { transform: translateY(-1px); }
        @keyframes asgnFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .asgn-item { animation: asgnFadeIn 0.25s ease-out both; }
        @keyframes asgn-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <Toast ref={toast} />

      <div style={{ background: C.bg, minHeight: '100%' }}>

        {/* ── Page header ─────────────────────────────────────────── */}
        <div style={{
          background:    `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 60%, ${C.navyDark} 100%)`,
          borderRadius:  '20px',
          padding:       '28px 32px',
          marginBottom:  '24px',
          color:         C.white,
          position:      'relative',
          overflow:      'hidden',
          boxShadow:     '0 4px 18px rgba(14,34,93,0.25)',
        }}>
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '10px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                <i className="pi pi-file-edit" style={{ fontSize: '0.7rem' }} />
                Assignments
              </div>
              <h1 style={{ margin: '0 0 4px 0', fontSize: '1.65rem', fontWeight: '700', color: C.white, letterSpacing: '-0.4px' }}>
                Assignments
              </h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
                {pagination.total > 0
                  ? `${pagination.total} assignment${pagination.total !== 1 ? 's' : ''} available`
                  : 'Your pending and upcoming assignments'}
              </p>
            </div>

            {pagination.total > 0 && (
              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '14px 20px', textAlign: 'center', backdropFilter: 'blur(10px)' }}>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: '800', color: C.gold, lineHeight: 1 }}>{pagination.total}</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Batch filter chips ───────────────────────────────────── */}
        {(batches.length > 0 || batchesLoading) && (
          <div style={{
            background:    C.white,
            borderRadius:  '12px',
            border:        `1px solid ${C.border}`,
            padding:       '12px 16px',
            marginBottom:  '20px',
            boxShadow:     '0 1px 4px rgba(14,34,93,0.05)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

              {/* Label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <i className="pi pi-users" style={{ fontSize: '0.72rem', color: C.muted }} />
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Batch
                </span>
              </div>

              {/* Divider */}
              <div style={{ width: '1px', height: '16px', background: C.border, flexShrink: 0 }} />

              {/* Chips — shimmer while loading, real chips once loaded */}
              {batchesLoading ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[80, 110, 95, 70].map((w, i) => (
                    <div
                      key={i}
                      style={{
                        width:           `${w}px`,
                        height:          '28px',
                        borderRadius:    '20px',
                        background:      'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        backgroundSize:  '200% 100%',
                        animation:       'asgn-shimmer 1.4s infinite',
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="asgn-batch-chips">

                  {/* "All Batches" chip */}
                  <button
                    className="asgn-batch-chip"
                    onClick={() => handleBatchSelect(null)}
                    style={{
                      padding:      '4px 14px',
                      borderRadius: '20px',
                      fontSize:     '0.75rem',
                      fontWeight:   '700',
                      cursor:       'pointer',
                      border:       selectedBatch === null ? 'none' : `1px solid ${C.border}`,
                      background:   selectedBatch === null ? C.navy : 'transparent',
                      color:        selectedBatch === null ? C.gold : C.sub,
                      boxShadow:    selectedBatch === null ? '0 2px 8px rgba(14,34,93,0.2)' : 'none',
                    }}
                  >
                    All Batches
                  </button>

                  {batches.map(b => {
                    const isActive = selectedBatch === b.batch_id;
                    return (
                      <button
                        key={b.batch_id}
                        className="asgn-batch-chip"
                        onClick={() => handleBatchSelect(b.batch_id)}
                        style={{
                          padding:      '4px 14px',
                          borderRadius: '20px',
                          fontSize:     '0.75rem',
                          fontWeight:   '700',
                          cursor:       'pointer',
                          border:       isActive ? 'none' : `1px solid ${C.border}`,
                          background:   isActive ? C.navy : 'transparent',
                          color:        isActive ? C.gold : C.sub,
                          boxShadow:    isActive ? '0 2px 8px rgba(14,34,93,0.2)' : 'none',
                          display:      'inline-flex',
                          alignItems:   'center',
                          gap:          '5px',
                        }}
                      >
                        {isActive && (
                          <i className="pi pi-check" style={{ fontSize: '0.62rem' }} />
                        )}
                        {b.batch_name}
                        {b.course_name && (
                          <span style={{
                            fontSize:   '0.65rem',
                            fontWeight: '500',
                            opacity:    0.7,
                            marginLeft: '1px',
                          }}>
                            · {b.course_name}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Not enrolled notice ─────────────────────────────────── */}
        {!isEnrolled && batches.length === 0 && (
          <div style={{ display: 'flex', gap: '12px', padding: '14px 16px', background: 'rgba(8,145,178,0.08)', border: '1px solid rgba(8,145,178,0.25)', borderLeft: '4px solid #0891b2', borderRadius: '12px', marginBottom: '20px' }}>
            <i className="pi pi-info-circle" style={{ fontSize: '1.1rem', color: '#0891b2', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: '0 0 2px 0', fontSize: '0.83rem', fontWeight: '700', color: C.navy }}>Not Enrolled in a Batch</p>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#0891b2', lineHeight: 1.5 }}>
                Showing all general assignments. Enroll in a batch to receive batch-specific assignments.
              </p>
            </div>
          </div>
        )}

        {/* ── Active batch filter badge ────────────────────────────── */}
        {selectedBatch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div style={{
              display:    'inline-flex',
              alignItems: 'center',
              gap:        '5px',
              padding:    '4px 10px',
              borderRadius: '8px',
              background:   C.goldDim,
              border:       `1px solid ${C.goldBorder}`,
              fontSize:     '0.75rem',
              fontWeight:   '600',
              color:        '#8a6000',
            }}>
              <i className="pi pi-filter-fill" style={{ fontSize: '0.65rem' }} />
              {batches.find(b => b.batch_id === selectedBatch)?.batch_name ?? 'Batch'}
              <button
                onClick={() => handleBatchSelect(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 2px', color: '#8a6000', display: 'flex', alignItems: 'center' }}
                title="Clear batch filter"
              >
                <i className="pi pi-times" style={{ fontSize: '0.62rem' }} />
              </button>
            </div>
          </div>
        )}

        {/* ── Content ─────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="3" animationDuration=".8s" />
              </div>
              <p style={{ margin: 0, color: C.navy, fontSize: '0.92rem', fontWeight: '600' }}>Loading assignments…</p>
            </div>
          </div>
        ) : assignments.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '52px 26px', textAlign: 'center' }}>
            <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <i className="pi pi-file" style={{ fontSize: '1.6rem', color: C.navy }} />
            </div>
            <p style={{ margin: '0 0 6px 0', fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>No Assignments</p>
            <p style={{ margin: 0, color: C.muted, fontSize: '0.85rem', lineHeight: 1.6, maxWidth: '280px' }}>
              {selectedBatch
                ? `No assignments for ${batches.find(b => b.batch_id === selectedBatch)?.batch_name ?? 'this batch'}`
                : 'No assignments available at the moment.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '18px' }}>
            {assignments.map((a, i) => (
              <div key={a.assignment_id} className="asgn-item" style={{ animationDelay: `${i * 60}ms` }}>
                <AssignmentCard assignment={a} onPress={handleAssignmentPress} />
              </div>
            ))}
          </div>
        )}

        {/* ── Pagination footer ───────────────────────────────────── */}
        {pagination.pages > 1 && !loading && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginTop: '24px', padding: '14px 18px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)' }}>
            <span style={{ fontSize: '0.8rem', color: C.muted }}>
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} assignments
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <button onClick={() => setPage(1)} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
              </button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
              </button>

              {renderPagination()}

              <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
              </button>
              <button onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}>
                <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Assignments;